import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {AccountService} from '../account.service';
import {BaseComponent} from '../../core/base.component';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../notification/notification.service';
import {AccountUser} from '../../api/models/account-user';
import {LookupModel} from '../../api/models/lookup-model';
import {AuthService} from '../../core/security/auth.service';
import {UsersComponent} from '../users/users.component';
import {RolesEnum} from '../../core/security/roles.enum';
import {CurrentUser} from '../../api/models/current-user';
import {ModulesEnum} from '../../core/security/modules.enum';
import {BasicSoftphoneService as BasicSoftphoneApiService} from '../../api/services/basic-softphone.service';
import {BasicSoftPhoneSummaryModel} from '../../api/models/basic-soft-phone-summary-model';
import {BasicSoftPhoneChangeExtensionPasswordModel} from '../../api/models/basic-soft-phone-change-extension-password-model';
import {MatConfirmDialogComponent} from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {LookupService} from '../../api/services/lookup.service';
import {BasicSoftPhoneSaveSettingsModel} from '../../api/models/basic-soft-phone-save-settings-model';
import {Subscription} from 'rxjs';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { NgIf } from '@angular/common';
import { MatTextInputComponent } from '../../editors/mat-text-input/mat-text-input.component';
import { MatPhoneNumberInputComponent } from '../../editors/mat-phone-number-input/mat-phone-number-input.component';
import { MatNumberInputComponent } from '../../editors/mat-number-input/mat-number-input.component';
import { MatSimpleSelectComponent } from '../../editors/mat-simple-select/mat-simple-select.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatCheckboxInputComponent } from '../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { FlexModule } from '@angular/flex-layout';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatCard,
        MatCardTitle,
        MatCardContent,
        NgIf,
        MatTextInputComponent,
        MatPhoneNumberInputComponent,
        MatNumberInputComponent,
        MatSimpleSelectComponent,
        MatCardActions,
        MatButton,
        MatTooltip,
        Angular2PromiseButtonModule,
        MatCheckboxInputComponent,
        FlexModule,
        MatIcon,
    ],
})
export class UserEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  form: UntypedFormGroup;
  usernameControl = new UntypedFormControl('', [Validators.email, Validators.required]);
  emailControl = new UntypedFormControl('', [Validators.email, Validators.required]);
  enableCounterPathControl = new UntypedFormControl(false);
  enableCounterPathInitialValue = false;
  submitted: boolean;

  serverError: boolean;
  editModel: AccountUser = {id: 'new', approved: true, isLocked: true, emailConfirmed: false, emailNotificationEnabled: true};
  basicSoftPhoneSummary: BasicSoftPhoneSummaryModel = {};
  roles: LookupModel[] = [];
  disabledRoles: LookupModel[] = [];
  buttonSubmitPromise: Promise<void>;
  hasSoftPhoneModule: boolean;
  hasProMessagingModule = false;

  hasBasicSoftPhoneModule: boolean;
  hasBasicSoftPhoneError: boolean;

  sendQrCodePromise: Promise<void>;
  isEditorAdmin = false;
  adminRoleId = '-1';

  buttonBasicSoftPhoneSettingsSubmitPromise: Promise<void>;
  smsPhoneNumbers: LookupModel[] = [];
  basicSoftPhoneSettingForm: FormGroup<{ smsNumberId: FormControl }>;

  private paramsSubscription;
  private id: string;
  private enableCounterPathControlValueSubscription: Subscription;

  constructor(route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService,
              public dialog: MatDialog,
              private softphoneApiService: BasicSoftphoneApiService,
              private accountService: AccountService,
              private fb: UntypedFormBuilder,
              private lookupService: LookupService,
              private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);

    this.paramsSubscription = this.route.params.subscribe(params => {
      this.id = params['id'];
      console.log(this.id);
    });

    this.editRoles = [RolesEnum.AccountUsers_Edit, RolesEnum.AccountUsers_Delete];
    this.deleteRoles = [RolesEnum.AccountUsers_Delete];

    this.headerService.showBackButton(true);
    this.createForm();
  }

  createForm(): void {

    this.basicSoftPhoneSettingForm = new FormGroup({
      // extension: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
      smsNumberId: new FormControl('', {nonNullable: false}),
    });

    this.form = this.fb.group({
        'username': this.usernameControl,
        'firstName': [''],
        'lastName': [''],
        'email': this.emailControl,
        'phoneNumber': [''],
        'extension': [''],
        'roleId': ['', [Validators.required]],
        'isLocked': [''],
        'emailConfirmed': [''],
        'emailNotificationEnabled': [true],
        'approved': [true],
        'enableCounterPath': this.enableCounterPathControl,
        'resetSoftPhonePassword': [false],
      }
    );
  }

  async onSubmit() {
    this.serverError = false;
    if (this.form.valid && this.hasEditAccess) {
      this.buttonSubmitPromise = this.processSubmit();
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();
      await this.accountService.saveAccountUser(reqData);
      this.notificationService.notifySuccess('User updated successfully');
      this.headerService.goBack();
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): AccountUser {
    const formModel = this.form.getRawValue();

    return {
      id: this.editModel.id,
      username: formModel.username,
      firstName: formModel.firstName,
      lastName: formModel.lastName,
      phoneNumber: formModel.phoneNumber,
      extension: formModel.extension,
      email: formModel.email,
      roleId: formModel.roleId,
      isLocked: formModel.isLocked,
      emailConfirmed: formModel.emailConfirmed,
      emailNotificationEnabled: formModel.emailNotificationEnabled,
      approved: formModel.approved,
      enableCounterPath: formModel.enableCounterPath,
      enableLdap: formModel.enableCounterPath,
      resetSoftPhonePassword: formModel.resetSoftPhonePassword,
    };
  }

  onCurrentUserChanged(user: CurrentUser): void {
    if (user) {
      this.isEditorAdmin = user.isAdmin;
      this.hasSoftPhoneModule = user.modules.indexOf(ModulesEnum.SoftPhoneModule) > -1;
      this.hasBasicSoftPhoneModule = user.modules.indexOf(ModulesEnum.BasicSoftPhoneModule) > -1;
    } else {
      this.hasSoftPhoneModule = false;
      this.hasBasicSoftPhoneModule = false;
      this.isEditorAdmin = false;
    }

    this.hasProMessagingModule = this.authService.userHasPackage(ModulesEnum.ProMessagingModule);
  }

  async onBasicSoftPhoneSubmit() {
    this.hasBasicSoftPhoneError = false;

    this.submitted = true;
    this.sendQrCodePromise = this.processBasicSoftPhoneSubmit();

  }

  async processBasicSoftPhoneSubmit(): Promise<void> {

    try {
      await this.sendSoftPhoneQrCode()
      this.notificationService.notifySuccess('SoftPhone login information successfully sent');
    } catch (e) {
      this.hasBasicSoftPhoneError = true;
    } finally {
      this.submitted = false;
    }
  }

  async sendSoftPhoneQrCode(): Promise<boolean> {
    await this.softphoneApiService.basicSoftphoneSendQrCode({customerUserId: this.editModel.id})
      .toPromise();

    return true;
  }

  async changeSoftPhonePassword(model: BasicSoftPhoneChangeExtensionPasswordModel): Promise<boolean> {
    await this.softphoneApiService.basicSoftphoneChangeExtensionPassword({body: model})
      .toPromise();

    return true;
  }

  async ngOnInit() {
    super.ngOnInit();
    this.enableCounterPathInitialValue = false;

    this.enableCounterPathControlValueSubscription = this.enableCounterPathControl.valueChanges.subscribe((value) => {
      if (this.enableCounterPathInitialValue == value) {
        return;
      }

      this.enableCounterPathInitialValue = value;

      if (!this.editModel.enableCounterPath && value) {
        const dialogConfirmRef = this.dialog.open(MatConfirmDialogComponent, {
          width: '400px',
          // height: '90%',
          data: {
            title: 'Attention',
            actionTitle: 'I Agree',
            text: 'By enabling the TeleGo Desktop App for this user, your account will be charged a one time license fee of $50, plus a recurring monthly maintenance fee of $1.99.',
            hideCancel: false
          }
        });

        dialogConfirmRef.afterClosed().subscribe(async (confirmResult) => {
          if (confirmResult) {
            this.form.patchValue({
              resetSoftPhonePassword: true,
            });
          } else {
            this.form.patchValue({
              enableCounterPath: false,
              resetSoftPhonePassword: false,
            });
          }
        });
      }
    });

    this.editModel = await this.accountService.getUserById(this.id);
    await this.getSoftphoneSummary(this.editModel.extension);

    this.accountService.queryRolesLookup().then(([roles, total]) => {
      this.roles = roles;

      const adminRole = this.roles.find(x => x.text.toLowerCase() === 'administrator');
      if (adminRole) {
        if (!this.isEditorAdmin) {
          this.disabledRoles = [adminRole];
        }

        this.adminRoleId = `${adminRole.id}`;
      }
    });

    this.ngOnChanges();
  }

  ngOnChanges(changes?: SimpleChanges): void {
    this.form.reset({
      username: this.editModel.username,
      firstName: this.editModel.firstName,
      lastName: this.editModel.lastName,
      phoneNumber: this.editModel.phoneNumber,
      extension: this.editModel.extension ? parseInt(this.editModel.extension, 10) : undefined,
      email: this.editModel.email,
      roleId: this.editModel.roleId,
      isLocked: this.editModel.isLocked,
      emailConfirmed: this.editModel.emailConfirmed,
      emailNotificationEnabled: this.editModel.emailNotificationEnabled,
      approved: this.editModel.approved,
      enableCounterPath: this.editModel.enableCounterPath,
      resetSoftPhonePassword: this.editModel.resetSoftPhonePassword,
    });
  }

  async getSoftphoneSummary(extension: string): Promise<void> {

    this.basicSoftPhoneSummary = await this.softphoneApiService.basicSoftphoneExtensionSummary({
      extension: extension
    })
      .toPromise();

    this.smsPhoneNumbers = await this.getSmsNumbersLookup();

    this.basicSoftPhoneSettingForm.reset({
      // extension: this.summaryModel.extension,
      smsNumberId: this.basicSoftPhoneSummary.smsNumberId
    });
  }

  async getSmsNumbersLookup() {
    const result = await this.lookupService.lookupGetSmsNumbers({
      extension: this.basicSoftPhoneSummary.extension
    }).toPromise();

    result.data.splice(0, 0, {id: '', text: 'None'});

    return result.data;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.enableCounterPathControlValueSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  async onResendClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Resend Invitation?',
        text: 'Are you sure you want to resend invitation email?',
        actionTitle: 'Resend',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.accountService.resendInvitation(this.editModel.username);
        await this.notificationService.notifySuccess('Invitation sent successfully');
      }
    });
  }

  onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete User?',
        text: 'Are you sure you want to delete user?',
        actionTitle: 'Delete',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.accountService.deleteAccountUser(this.editModel.id);
        await this.notificationService.notifySuccess('User deleted successfully');

        await this.headerService.goToComponent(UsersComponent.name);
      }
    });
  }

  onBasicSoftPhoneSettingsSubmit() {
    this.hasBasicSoftPhoneError = false;

    if (this.form.valid) {
      this.buttonBasicSoftPhoneSettingsSubmitPromise = this.processBasicSoftPhoneSettingsSubmit();
    }
  }

  async processBasicSoftPhoneSettingsSubmit(): Promise<void> {
    this.submitted = true;
    const reqData = this.prepareBasicSoftPhoneSettingsFormModel();

    try {
      await this.softphoneApiService.basicSoftphoneSaveSoftPhoneSettings({body: reqData}).toPromise();
      this.notificationService.notifySuccess('SoftPhone settings successfully updated');
      await this.getSoftphoneSummary(this.editModel.extension);
      this.ngOnChanges();
    } catch (e) {
      console.log(e);
      this.hasBasicSoftPhoneError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareBasicSoftPhoneSettingsFormModel(): BasicSoftPhoneSaveSettingsModel {
    const formModel = this.basicSoftPhoneSettingForm.value;

    return {
      extension: this.editModel.extension,
      smsNumberId: formModel.smsNumberId
    };
  }

  updateEmailField() {
    const username = this.usernameControl.getRawValue();
    const oldEmail = this.emailControl.getRawValue();
    if (!oldEmail) {
      this.form.patchValue({
        email: username
      });
    }

  }
}
